/* eslint-disable react/no-array-index-key */
import React, { useState, useRef, useEffect } from 'react';
import Button from '@components/Button';
import useToggle from '@hooks/useToggle';
import IconClose from '@components/icons/IconClose';
import cn from '@utils/cn';
import { SeatsModal } from '../Header/SeatsModal';
import {
  useModifySeatSetting,
  useSeatConfig,
} from '../Header/SeatsModal/seatsApis';
import {
  $seatsSelectedApi,
  $seatsSpcialIdsApi,
} from '../Header/SeatsModal/seatsStores';
import type { SeatItem } from './types';

export function ShareBasicSeatsSetting() {
  const modal = useToggle(false);
  const { data, isLoading, refetch } = useSeatConfig({
    onSuccess: ({ list, operatorId, ownerId }) => {
      $seatsSelectedApi.init(list);
      $seatsSpcialIdsApi.set({
        operatorId,
        ownerId,
      });
    },
  });
  const modify = useModifySeatSetting();

  const list = data?.list ?? [];

  const renderContent = () => {
    if (isLoading) return null;
    if (list.length === 0) {
      return (
        <div className="ml-5 select-none">仅企业所有者和作品归属者可以访问</div>
      );
    }
    return (
      <ItemList
        data={list}
        isLoading={modify.isLoading}
        onItemClick={async (id) => {
          if (isLoading) return;
          await modify.mutateAsync({
            addData: [],
            removeData: [id],
          });
          refetch();
        }}
        onMoreClick={modal.on}
      />
    );
  };

  return (
    <div className="flex w-[665px] items-center">
      <div className="shrink-0">访问成员：</div>
      <div className="ml-20 flex h-40 min-w-0 flex-1 items-center rounded border bg-white px-5">
        {renderContent()}
      </div>
      <Button className="ml-10" size="lg" onClick={modal.on}>
        选择成员
      </Button>
      {modal.isOn && (
        <SeatsModal
          initialData={list}
          onRequsetClose={() => {
            modal.off();
            refetch();
          }}
        />
      )}
    </div>
  );
}

const MORE_WIDTH = 32;
const GAP = 5;

function ItemList({
  data,
  isLoading,
  onItemClick,
  onMoreClick,
}: {
  data: SeatItem[];
  isLoading: boolean;
  onItemClick: (id: SeatItem['id']) => void;
  onMoreClick: () => void;
}) {
  const [elementsToRender, setElementsToRender] = useState<number>(data.length);
  const containerRef = useRef<HTMLDivElement>(null);
  const refs = useRef<(HTMLDivElement | null)[]>(Array(data.length).fill(null));
  const [isCalculating, setIsCalculating] = useState(true);

  useEffect(() => {
    // 重新设置refs数组的长度
    refs.current = refs.current.slice(0, data.length);
    // 为新增的元素添加null引用
    while (refs.current.length < data.length) {
      refs.current.push(null);
    }
  }, [data]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsCalculating(true);
      const maxWidth = containerRef.current?.offsetWidth || 0 - GAP;
      let widthSum = 0;
      let elementsCount = 0;
      const elementsWidth = refs.current.map((ref) => ref?.offsetWidth || 0);
      for (let i = 0; i < elementsWidth.length; i++) {
        widthSum += elementsWidth[i] + GAP;
        elementsCount += 1;
        if (widthSum > maxWidth) {
          for (let j = i; j >= 0; j--) {
            widthSum -= elementsWidth[j];
            elementsCount -= 1;
            if (widthSum <= maxWidth - MORE_WIDTH) break;
          }
          break;
        }
      }
      setElementsToRender(elementsCount);
      setIsCalculating(false);
    }, 0);
    return () => {
      clearTimeout(timerId);
    };
  }, [data]);

  const list = isCalculating ? data : data.slice(0, elementsToRender);

  return (
    <div
      className={cn('flex h-full w-full items-center', {
        invisible: isCalculating,
      })}
      ref={containerRef}
    >
      {list.map((x, index) => {
        const name = x.accessScope === 0 ? '企业全部成员' : x.relationName;
        return (
          <div
            key={x.id}
            className="mr-5 flex h-30 shrink-0 select-none items-center rounded bg-white-light px-10"
            ref={(ref) => {
              refs.current[index] = ref;
            }}
          >
            <div className="max-w-[84px] truncate" title={name}>
              {name}
            </div>
            <IconClose
              className={cn('ml-5', {
                'cursor-wait': isLoading,
              })}
              scheme="light"
              onClick={() => {
                onItemClick(x.id);
              }}
            />
          </div>
        );
      })}
      {elementsToRender < data.length && (
        <div
          style={{ width: MORE_WIDTH }}
          className="mr-5 flex h-30 shrink-0 cursor-pointer select-none items-center rounded bg-white-light px-10 hover:bg-[#efefef]"
          onClick={onMoreClick}
        >
          ...
        </div>
      )}
    </div>
  );
}

export default ShareBasicSeatsSetting;
